/**
 * ProfileUserSecuriteController — onglet « Sécurité » de
 * /profil-utilisateur.html.
 *
 * Pilote les actions critiques du compte non couvertes par les controllers
 * dédiés :
 *   - Changement d'email   → POST /auth/email-change-request
 *                            (puis ouverture mailto: support)
 *   - Cessation INPI §25.C → POST /organization/lifecycle/cessation-inpi
 *                            (modale raccourci, archivage en ligne par défaut)
 *
 * Le wizard résiliation complet (§25.B) est porté par
 * `ResignationWizardController` (modale dédiée). La suppression de compte
 * (RGPD §21) par `RgpdAccountDeletionController`.
 *
 * Vocabulaire UI : « PA » ou « Plateforme Agréée », « procedures.inpi.fr ».
 * Pas de « SuperPDP » ni « PDP ». NotaFacto invisible.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

export default class ProfileUserSecuriteController extends BaseController {
  static targets = [
    "cessationModal",
    "cessationConfirmBtn",
    "cessationError",
  ];

  // ---------------------------------------------------------------------------
  // Email change request — POST /auth/email-change-request + mailto
  // ---------------------------------------------------------------------------

  async requestEmailChange(event) {
    event.preventDefault();
    // En V1, la procédure est : on ouvre un mailto: pré-rempli vers
    // le support. Pas d'appel API direct depuis cet écran — la maquette
    // pointe déjà sur un mailto: dans block-email. Ce handler reste
    // disponible pour les pages qui voudraient enchaîner POST + mailto.
    try {
      const newEmail = (event.params?.newEmail ?? "").trim();
      if (newEmail) {
        await api.requestEmailChange({ newEmail });
        this.showSuccess("Demande de changement d'email envoyée au support.");
      }
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Erreur lors de la demande de changement d'email.";
      this.showError(msg);
    }
  }

  // ---------------------------------------------------------------------------
  // Modale cessation INPI §25.C — raccourci (archivage en ligne par défaut)
  // ---------------------------------------------------------------------------

  openCessationModal(event) {
    event.preventDefault();
    if (this.hasCessationModalTarget && typeof this.cessationModalTarget.showModal === "function") {
      this.cessationModalTarget.showModal();
    }
  }

  closeCessationModal(event) {
    if (event) event.preventDefault();
    if (this.hasCessationModalTarget && typeof this.cessationModalTarget.close === "function") {
      this.cessationModalTarget.close();
    }
  }

  async confirmCessation(event) {
    event.preventDefault();
    if (this.hasCessationConfirmBtnTarget) this.cessationConfirmBtnTarget.disabled = true;
    try {
      const data = await api.initiateInpiCessation({ archiveChoice: "online" });
      // Redirect sur procedures.inpi.fr (signature qualifiée FranceConnect+ / eIDAS).
      if (data && data.formality && data.formality.signatureUrl) {
        window.location.assign(data.formality.signatureUrl);
        return;
      }
      // V1 — placeholder LOCAL : pas de signature URL réelle, on confirme juste.
      this.showSuccess("Cessation d'activité initiée. La signature sur procedures.inpi.fr sera disponible dans une prochaine version.");
      this.closeCessationModal();
      window.location.reload();
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Erreur lors de l'initiation de la cessation INPI.";
      if (this.hasCessationErrorTarget) {
        this.cessationErrorTarget.textContent = msg;
        this.cessationErrorTarget.hidden = false;
      } else {
        this.showError(msg);
      }
    } finally {
      if (this.hasCessationConfirmBtnTarget) this.cessationConfirmBtnTarget.disabled = false;
    }
  }
}
